/**
 * viewCache — in-memory, stale-while-revalidate store for page/list data.
 *
 * Every new-ui route unmounts on navigation (see NewUIApp's <Routes>), so a
 * page's useState is gone the instant you leave it. Without a cache, coming
 * back refetches from zero and flashes a full-page spinner over content the
 * user was reading seconds ago. This module keeps the last successfully loaded
 * value for a view in a plain module-level Map — it survives unmount, but not a
 * hard page reload — so a page can seed its initial state from cache, render
 * INSTANTLY, and refresh silently in the background (stale-while-revalidate).
 *
 * Usage (per page/hook):
 *   const [rows, setRows] = useState(() => getView(KEY) ?? []);
 *   const [loading, setLoading] = useState(() => !hasView(KEY));
 *   // in the fetch effect:
 *   if (!hasView(KEY)) setLoading(true);          // spinner only on a true first load
 *   fetchRows()
 *     .then((list) => { setRows(list); setView(KEY, list); })
 *     .catch(() => { if (!hasView(KEY)) showError(); })  // keep stale on a failed refresh
 *     .finally(() => setLoading(false));
 *
 * Keys must fold in anything the value depends on (active company, filter,
 * page) so a different scope is a clean cache miss rather than stale bleed —
 * e.g. `drafts:${cin}` not just `drafts`.
 *
 * PRIVACY: the whole store is wiped on sign-out (AuthContext) so a second user
 * on the same tab never inherits the first user's cached lists. Nothing here is
 * persisted to disk — it lives only for the life of the page.
 */

const store = new Map();

/** Cached value for a key, or undefined if it has never been loaded. */
export const getView = (key) => store.get(key);

/** True once a key holds a cached value — the "is this a true first load?" test. */
export const hasView = (key) => store.has(key);

/** Record the latest successful load for a key. */
export const setView = (key, value) => { store.set(key, value); };

/** Drop a single view (e.g. after a mutation that invalidates it). */
export const clearView = (key) => { store.delete(key); };

/**
 * Drop every view whose key starts with `prefix`. Handy for evicting a whole
 * family at once, e.g. clearViewsByPrefix('drafts:') after a bulk change.
 */
export const clearViewsByPrefix = (prefix) => {
  for (const key of store.keys()) {
    if (String(key).startsWith(prefix)) store.delete(key);
  }
};

/** Wipe the entire store — called on sign-out. */
export const clearAllViews = () => { store.clear(); };
